import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Card, Typography } from 'antd'
import TimeColumn from './TimeColumn'
import DayColumn from './DayColumn'
import CurrentTimeLine from './CurrentTimeLine'
import { Todo } from '../types'
import { getWeekRangeString, generateTimeSlots, timeToMinutes, CALENDAR_START_MINUTE, CALENDAR_END_MINUTE, getCurrentTimeLinePosition, PIXELS_PER_15_MIN } from '../utils/timeUtils'
import { Settings } from '../types'
import Skeleton from 'antd/es/skeleton'
// ScrollbarOverlay 已移除：不显示任何滚动条

const { Title } = Typography

interface WeeklyCalendarProps {
  todos: Todo[]
  weekStart: Date
  onCreateTodo: (dayOfWeek: number, startTime: string, element: HTMLElement) => void
  onEditTodo: (todo: Todo, element: HTMLElement) => void
  onDeleteTodo: (id: string) => void
  onMoveTodo: (todoId: string, newDayOfWeek: number, newStartTime: string) => void
  onResizeTodo: (id: string, newHeight: number) => void
  onToggleComplete: (id: string, completed: boolean) => void
  loading: boolean
  settings: Settings
  mutatingMap: Record<string, boolean>
  onCalendarScroll?: () => void
}

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = ({
  todos,
  weekStart,
  onCreateTodo,
  onEditTodo,
  onDeleteTodo,
  onMoveTodo,
  onResizeTodo,
  onToggleComplete,
  loading,
  settings,
  mutatingMap,
  onCalendarScroll
}) => {
  const [currentTimePosition, setCurrentTimePosition] = useState(0)
  const [currentDayOfWeek, setCurrentDayOfWeek] = useState(1)
  const [currentHour, setCurrentHour] = useState<number | null>(null)
  const [currentMinute, setCurrentMinute] = useState<number>(0)
  const [isResizing, setIsResizing] = useState(false)

  // Calculate todos for each day
  const getTodosForDay = useCallback((dayOfWeek: number) => {
    return todos.filter(todo => todo.day_of_week === dayOfWeek)
  }, [todos])

  // Update current time line position
  useEffect(() => {
    const updateCurrentTime = () => {
      const position = getCurrentTimeLinePosition()
      setCurrentTimePosition(position)

      // Get current day of week (1-7, Monday to Sunday)
      const now = new Date()
      const dayOfWeek = now.getDay() // 0-6, Sunday-Saturday
      const convertedDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek // Convert Sunday from 0 to 7
      setCurrentDayOfWeek(convertedDayOfWeek)
      setCurrentHour(now.getHours())
      setCurrentMinute(now.getMinutes())
    }

    updateCurrentTime()
    const interval = setInterval(updateCurrentTime, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [])

  // Handle cell click
  const handleCellClick = (dayOfWeek: number, time: string, element: HTMLElement) => {
    onCreateTodo(dayOfWeek, time, element)
  }

  const bodyRef = useRef<HTMLDivElement | null>(null)

  // 移除滚动状态与覆盖滚动条逻辑

  // Close modal on calendar scroll (except when resizing)
  useEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const handleScroll = () => {
      if (!isResizing && onCalendarScroll) {
        onCalendarScroll()
      }
    }
    el.addEventListener('scroll', handleScroll)
    return () => {
      el.removeEventListener('scroll', handleScroll)
    }
  }, [onCalendarScroll, isResizing])

  if (loading && todos.length === 0) {
    return (
      <Card variant="borderless">
        <Skeleton active />
      </Card>
    )
  }

  return (
    <div className="weekly-calendar">
      <div className="calendar-header">
        <Title level={4} className="week-range">
          {getWeekRangeString(weekStart)}
        </Title>
        <div className="week-navigation">
          {/* Navigation buttons will be moved to parent component later if needed */}
        </div>
      </div>

      <div className="calendar-days-header">
        <div className="time-header-spacer"></div>
        {Array.from({ length: 7 }, (_, index) => {
          const dayOfWeek = index + 1 // 1-7 (Monday to Sunday)
          const dayDate = new Date(weekStart)
          dayDate.setDate(dayDate.getDate() + index)
          const dayName = dayDate.toLocaleDateString('zh-CN', { weekday: 'short' })

          return (
            <div
              key={dayOfWeek}
              className={`calendar-day-header ${new Date().toDateString() === dayDate.toDateString() ? 'today' : ''}`}
            >
              <div className="day-name">{dayName}</div>
              <div className="day-date">{dayDate.getMonth() + 1}/{dayDate.getDate()}</div>
            </div>
          )
        })}
      </div>

      <div
        ref={bodyRef}
        className="calendar-body"
      >
        <TimeColumn
          timeSlots={generateTimeSlots}
          highlightHour={currentHour}
          hideHighlightLabel={Math.min(currentMinute, 60 - currentMinute) < 15}
        />

        <div
          className="days-container"
          style={{ position: 'relative', height: (generateTimeSlots.length - 1) * PIXELS_PER_15_MIN }}
        >
          {Array.from({ length: 7 }, (_, index) => {
            const dayOfWeek = index + 1 // 1-7 (Monday to Sunday)
            const dayTodos = getTodosForDay(dayOfWeek)
            const dayDate = new Date(weekStart)
            dayDate.setDate(dayDate.getDate() + index)

            return (
              <DayColumn
                key={dayOfWeek}
                dayOfWeek={dayOfWeek}
                dayDate={dayDate}
                timeSlots={generateTimeSlots}
                todos={dayTodos}
                onCellClick={handleCellClick}
                onEditTodo={onEditTodo}
                onDeleteTodo={onDeleteTodo}
                onMoveTodo={onMoveTodo}
                onResizeTodo={onResizeTodo}
              onToggleComplete={onToggleComplete}
              settings={settings}
              isResizing={isResizing}
              onResizingChange={setIsResizing}
              mutatingMap={mutatingMap}
            />
          )
        })}
          {(() => {
            // Check if current time is within calendar range (07:00-24:00)
            const now = new Date()
            const currentMinutes = now.getHours() * 60 + now.getMinutes()
            const showCurrent = currentMinutes >= CALENDAR_START_MINUTE && currentMinutes <= CALENDAR_END_MINUTE
            
            return showCurrent ? (
              <CurrentTimeLine 
                position={currentTimePosition} 
                currentDayOfWeek={currentDayOfWeek}
              />
            ) : null
          })()}
          {/* 已移除滚动条覆盖层 */}
        </div>
      </div>
    </div>
  )
}

export default WeeklyCalendar